/**
 * _app/settings layout — two-pane island.
 *
 * The layout owns the <Island> — child routes render their body
 * into the `<Outlet />` inside `<SettingsShell>`, NOT a new Island.
 * The breadcrumb is computed dynamically from the current pathname
 * using topology data so deep URLs like
 * `/app/settings/logs/ss_abc` show `Settings › Developer › Logs ›
 * ss_abc`.
 */

import type { BreadcrumbEntry } from '@/components/shell/Breadcrumb';
import { Island } from '@/components/shell/Island';
import { SettingsShell } from '@/components/shell/SettingsShell';
import { SiteSwitcherCrumb } from '@/components/shell/SiteSwitcherCrumb';
import { topology } from '@/lib/topology';
import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/settings')({
  // Settings renders in the demo too — like-for-like with the real
  // product. Pages show read-only fixture/shim data and every write is
  // blocked with a friendly error by the demo API layer, so nothing
  // here needs a redirect. (Theme on Preferences → Appearance is the
  // one setting demo users can genuinely change — it's per-device.)
  component: SettingsLayout,
});

function buildBreadcrumb(pathname: string): BreadcrumbEntry[] {
  // Every settings page leads with the site switcher crumb — same
  // chrome pattern as the site-scoped routes. Settings content
  // itself is org-scoped (workspace, members, billing, etc.), but
  // showing the switcher keeps "switch site" reachable from every
  // page so users don't have to climb back to `/` for it.
  const siteCrumb: BreadcrumbEntry = { label: 'Site', render: <SiteSwitcherCrumb /> };

  // Walk topology.settings looking for the entry whose href prefix
  // matches. `logs/:auditId` matches the "Logs" entry (prefix match).
  for (const section of topology.settings) {
    for (const item of section.items) {
      if (pathname === item.href || pathname.startsWith(`${item.href}/`)) {
        const trail: BreadcrumbEntry[] = [
          siteCrumb,
          { label: 'Settings', href: '/settings' },
          { label: section.header, href: item.href },
          { label: item.label, href: item.href },
        ];
        // If we're on a deeper path (logs/:auditId), append the leaf.
        if (pathname !== item.href) {
          const leaf = pathname.slice(item.href.length + 1);
          // Drop trailing slash.
          const clean = leaf.replace(/\/+$/, '');
          if (clean.length > 0) trail.push({ label: clean });
        }
        return trail;
      }
    }
  }
  // Fallback — bare /app/settings (should redirect to /profile via
  // the index route, but if it briefly renders, don't blow up).
  return [siteCrumb, { label: 'Settings' }];
}

function SettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const crumb = buildBreadcrumb(pathname);

  return (
    <Island breadcrumb={crumb}>
      <SettingsShell>
        <Outlet />
      </SettingsShell>
    </Island>
  );
}
